import { AlertTriangle, CheckCircle2, ShieldCheck, ZoomIn } from "lucide-react";
import type { CSSProperties } from "react";

import {
  canonicalizeOrcid,
  formatReferenceEntry,
  numberSections,
  STRUCTURED_ABSTRACT_FIELDS,
  tokenizeStructuredLabels,
  type Manuscript,
  type ManuscriptValidation,
} from "../domain/manuscript";
import {
  templateToCssVariables,
  type ManuscriptTemplate,
} from "../domain/templates";
import { EmphasizedText } from "./EmphasizedText";

type PreviewPanelProps = {
  manuscript: Manuscript;
  template: ManuscriptTemplate;
  validation: ManuscriptValidation;
  zoom: number;
  onZoomChange: (zoom: number) => void;
};

function Paragraphs({
  text,
  structured = false,
  className = "",
  showPlaceholder = true,
  template,
}: {
  text: string;
  structured?: boolean;
  className?: string;
  showPlaceholder?: boolean;
  template: ManuscriptTemplate;
}) {
  if (!text.trim()) {
    return showPlaceholder ? (
      <p className="paper-placeholder">尚未填写内容</p>
    ) : null;
  }
  const paragraphs = structured ? text.split(/\n+/) : text.split(/\n\s*\n/);
  return paragraphs
    .filter(Boolean)
    .map((paragraph, index) => (
      <p className={className} key={`${index}-${paragraph.slice(0, 20)}`}>
        {structured
          ? tokenizeStructuredLabels(paragraph).map((segment, segmentIndex) =>
              segment.bold ? (
                <strong key={`${segmentIndex}-${segment.text}`}>
                  <EmphasizedText
                    rules={template.emphasisRules}
                    text={segment.text}
                  />
                </strong>
              ) : (
                <span key={`${segmentIndex}-${segment.text}`}>
                  <EmphasizedText
                    rules={template.emphasisRules}
                    text={segment.text}
                  />
                </span>
              ),
            )
          : <EmphasizedText rules={template.emphasisRules} text={paragraph} />}
      </p>
    ));
}

export function PreviewPanel({
  manuscript,
  template,
  validation,
  zoom,
  onZoomChange,
}: PreviewPanelProps) {
  const variables = templateToCssVariables(template) as CSSProperties;
  const sections = numberSections(manuscript.sections, template.numbering);
  const affiliationNumbers = new Map(
    manuscript.affiliations.map((affiliation, index) => [affiliation.id, index + 1]),
  );
  const structuredAuthors = manuscript.authors.filter((author) => author.name.trim());
  const correspondingAuthors = structuredAuthors.filter(
    (author) => author.corresponding,
  );
  const orcidAuthors = structuredAuthors
    .map((author) => ({
      name: author.name,
      orcid: canonicalizeOrcid(author.orcid),
    }))
    .filter((author) => author.orcid);
  const blocker = validation.counts.blocker > 0;
  const warning = validation.counts.warning > 0;

  return (
    <aside className="preview-panel" aria-label="版式预览与检查">
      <div className="preview-toolbar">
        <div>
          <span className="eyebrow">LIVE PREVIEW</span>
          <h2>{template.page.size} 版式近似预览</h2>
        </div>
        <label className="zoom-control">
          <ZoomIn size={14} />
          <input
            aria-label="预览缩放"
            max="62"
            min="38"
            onChange={(event) => onZoomChange(Number(event.target.value))}
            type="range"
            value={zoom}
          />
          <span>{zoom}%</span>
        </label>
      </div>

      <div className="paper-viewport">
        <div
          className="paper-scale"
          style={{ ...variables, transform: `scale(${zoom / 100})` }}
        >
          <article className="paper-page">
            <header className="paper-title-page">
              <h1 className={!manuscript.metadata.title ? "paper-placeholder" : ""}>
                {manuscript.metadata.title || "Manuscript title"}
              </h1>
              {manuscript.metadata.runningTitle.trim() ? (
                <p className="paper-running-title">
                  Running title: {manuscript.metadata.runningTitle}
                </p>
              ) : null}
              {structuredAuthors.length ? (
                <p className="paper-authors">
                  {structuredAuthors.map((author, index) => {
                    const markers = author.affiliationIds
                      .map((id) => affiliationNumbers.get(id))
                      .filter((value): value is number => Boolean(value))
                      .map(String);
                    if (author.equalContribution) markers.push("#");
                    if (author.corresponding) markers.push("*");
                    return (
                      <span className="paper-author" key={author.id}>
                        {index ? "; " : ""}
                        {author.name}
                        {author.degrees ? `, ${author.degrees}` : ""}
                        {markers.length ? <sup>{markers.join(",")}</sup> : null}
                      </span>
                    );
                  })}
                </p>
              ) : (
                <p className={`paper-authors ${!manuscript.metadata.authors ? "paper-placeholder" : ""}`}>
                  {manuscript.metadata.authors || "Authors not entered"}
                </p>
              )}
              {manuscript.affiliations.length ? (
                manuscript.affiliations.map((affiliation, index) => (
                  <p className="paper-affiliations" key={affiliation.id}>
                    {index + 1} {affiliation.name || `Affiliation ${index + 1}`}
                  </p>
                ))
              ) : (
                <p className={`paper-affiliations ${!manuscript.metadata.affiliations ? "paper-placeholder" : ""}`}>
                  {manuscript.metadata.affiliations || "Affiliations not entered"}
                </p>
              )}
              {structuredAuthors.filter((author) => author.equalContribution).length >= 2 ? (
                <p className="paper-correspondence">
                  # These authors contributed equally.
                </p>
              ) : null}
              {correspondingAuthors.length === 1 ? (
                <p className="paper-correspondence">
                  * Corresponding author. {correspondingAuthors[0].name}
                  {correspondingAuthors[0].email
                    ? ` (${correspondingAuthors[0].email})`
                    : ""}
                </p>
              ) : correspondingAuthors.length > 1 ? (
                <p className="paper-correspondence">
                  * Corresponding author. {" "}
                  {correspondingAuthors
                    .map(
                      (author) =>
                        `${author.name}${author.email ? ` (${author.email})` : ""}`,
                    )
                    .join("; ")} are co-corresponding authors.
                </p>
              ) : null}
              {orcidAuthors.length ? (
                <p className="paper-correspondence">
                  ORCID — {orcidAuthors
                    .map((author) => `${author.name}: ${author.orcid}`)
                    .join("; ")}
                </p>
              ) : null}
              {manuscript.metadata.correspondence ? (
                <p className="paper-correspondence">
                  {manuscript.metadata.correspondence}
                </p>
              ) : null}
            </header>

            <section className="paper-abstract">
              <h2>Abstract</h2>
              {manuscript.abstract.mode === "structured" &&
              STRUCTURED_ABSTRACT_FIELDS.some(({ key }) =>
                manuscript.abstract.structured[key].trim(),
              ) ? (
                STRUCTURED_ABSTRACT_FIELDS.map(({ key, label }) => (
                  <p className="paper-abstract-paragraph" key={key}>
                    <strong>
                      <EmphasizedText
                        rules={template.emphasisRules}
                        text={`${label}:`}
                      />
                    </strong>{" "}
                    {manuscript.abstract.structured[key] ? (
                      <EmphasizedText
                        rules={template.emphasisRules}
                        text={manuscript.abstract.structured[key]}
                      />
                    ) : (
                      <span className="paper-placeholder">尚未填写</span>
                    )}
                  </p>
                ))
              ) : (
                <Paragraphs
                  className="paper-abstract-paragraph"
                  structured={manuscript.abstract.mode === "structured"}
                  template={template}
                  text={manuscript.abstract.content}
                />
              )}
              <p className={manuscript.keywords ? "paper-keywords" : "paper-placeholder"}>
                <strong>
                  <EmphasizedText
                    rules={template.emphasisRules}
                    text="Keywords:"
                  />
                </strong>{" "}
                {manuscript.keywords ? (
                  <EmphasizedText
                    rules={template.emphasisRules}
                    text={manuscript.keywords}
                  />
                ) : (
                  "Keywords not entered"
                )}
              </p>
            </section>

            {sections.map((section) => {
              const Heading = `h${Math.min(section.level + 1, 4)}` as "h2" | "h3" | "h4";
              return (
                <section className={`paper-section h${section.level}`} key={section.id}>
                  <Heading>
                    <EmphasizedText
                      rules={template.emphasisRules}
                      text={section.displayTitle || "Untitled section"}
                    />
                  </Heading>
                  <Paragraphs
                    showPlaceholder={false}
                    template={template}
                    text={section.content}
                  />
                </section>
              );
            })}

            {manuscript.tables.length ? (
              <section className="paper-section paper-tables">
                <h2>Tables</h2>
                {manuscript.tables.map((table, tableIndex) => (
                  <div className="paper-table-block" key={table.id}>
                    <p className="paper-table-title">
                      <strong>
                        <EmphasizedText
                          rules={template.emphasisRules}
                          text={`Table ${tableIndex + 1}.`}
                        />
                      </strong>{" "}
                      <EmphasizedText
                        rules={template.emphasisRules}
                        text={table.title}
                      />
                    </p>
                    <table className="paper-three-line-table">
                      <tbody>
                        {table.rows.map((row, rowIndex) => (
                          <tr key={`${table.id}-row-${rowIndex}`}>
                            {row.map((cell, columnIndex) => {
                              const Cell = rowIndex === 0 ? "th" : "td";
                              return (
                                <Cell key={`${table.id}-${rowIndex}-${columnIndex}`}>
                                  <EmphasizedText
                                    rules={template.emphasisRules}
                                    text={cell}
                                  />
                                </Cell>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {table.notes ? (
                      <p className="paper-table-note">
                        <strong>
                          <EmphasizedText
                            rules={template.emphasisRules}
                            text="Note:"
                          />
                        </strong>{" "}
                        <EmphasizedText
                          rules={template.emphasisRules}
                          text={table.notes}
                        />
                      </p>
                    ) : null}
                  </div>
                ))}
              </section>
            ) : null}

            {manuscript.figures.length ? (
              <section className="paper-section paper-figures">
                <h2>Figure legends</h2>
                {manuscript.figures.map((figure, figureIndex) => {
                  const title = figure.title.trim();
                  const separator = title && !/[.!?。！？]$/.test(title) ? ". " : " ";
                  return (
                    <p className="paper-figure-caption" key={figure.id}>
                      <strong>
                        <EmphasizedText
                          rules={template.emphasisRules}
                          text={`Figure ${figureIndex + 1}.${title ? ` ${title}` : ""}`}
                        />
                      </strong>
                      {figure.legend ? (
                        <>
                          {separator}
                          <EmphasizedText
                            rules={template.emphasisRules}
                            text={figure.legend}
                          />
                        </>
                      ) : null}
                    </p>
                  );
                })}
              </section>
            ) : null}

            <section className="paper-section paper-references">
              <h2>References</h2>
              {manuscript.references.length ? (
                manuscript.references.map((reference, index) => (
                  <p className="paper-reference" key={reference.id}>
                    <EmphasizedText
                      rules={template.emphasisRules}
                      text={
                        formatReferenceEntry(
                          reference,
                          index,
                          template.references.numbering,
                        ) || "Reference details not entered"
                      }
                    />
                  </p>
                ))
              ) : (
                <p className="paper-placeholder">尚未填写内容</p>
              )}
            </section>
          </article>
        </div>
      </div>

      <div className="validation-card">
        <div className="validation-summary">
          <div className={`validation-icon ${blocker ? "blocked" : warning ? "warning" : "pass"}`}>
            {blocker ? (
              <AlertTriangle size={18} />
            ) : warning ? (
              <ShieldCheck size={18} />
            ) : (
              <CheckCircle2 size={18} />
            )}
          </div>
          <div>
            <strong>
              {blocker
                ? `${validation.counts.blocker} 项阻断`
                : warning
                  ? "可导出，但有待补信息"
                  : "结构检查通过"}
            </strong>
            <span>
              {validation.counts.warning} 警告 · {validation.counts.info} 提示
            </span>
          </div>
        </div>
        <ul>
          {validation.items.slice(0, 3).map((item) => (
            <li className={item.severity} key={`${item.code}-${item.sectionId ?? "root"}`}>
              {item.message}
            </li>
          ))}
          {validation.items.length > 3 ? (
            <li className="more">还有 {validation.items.length - 3} 项未展开</li>
          ) : null}
        </ul>
        <p className="preview-disclaimer">
          浏览器预览不等于 Word 最终分页；导出后仍会执行 DOCX 结构审计。
        </p>
      </div>
    </aside>
  );
}
